export class TrustCategories {
  constructor() {
    // Trust category calculation logic
  }

  // Helper to get excipient alert level
  private getExcipientAlertLevel(averageRisk: string): string {
    switch (averageRisk) {
      case 'Avoid': return 'danger';
      case 'Caution': return 'warning';
      case 'Safe': return 'safe';
      default: return 'safe';
    }
  }

  // Helper to get evaluation badge class and text
  private getEvaluationBadge(rating: any): { class: string; text: string } {
    if (!rating || rating === 'Unknown') return { class: 'unknown', text: 'UNKNOWN' };

    const ratingLower = rating.toString().toLowerCase();
    if (ratingLower === 'high' || rating >= 4) return { class: 'high', text: 'HIGH' };
    if (ratingLower === 'medium' || rating >= 3) return { class: 'ok', text: 'OK' };
    if (ratingLower === 'low' || rating >= 2) return { class: 'poor', text: 'POOR' };
    return { class: 'safe', text: 'SAFE' };
  }

  getTrustCategories(productData: any, brandData?: any): TrustCategory[] {
    const categories: TrustCategory[] = [];
    const product = productData?.product;
    const brand = brandData?.brand;

    console.log('getTrustCategories called with:', { product, brand });
    console.log('Brand trust_score_details check:', brand?.trust_score_details);

    // Inactive ingredients (based on product excipients)
    const averageExcipientRisk = !product?.excipients?.length
      ? 'None'
      : product.average_excipient_risk_rating || 'Safe';
    const excipientLevel = this.getExcipientAlertLevel(averageExcipientRisk);
    const excipientBadge = excipientLevel === 'safe' ? { class: 'safe', text: 'SAFE' } :
      excipientLevel === 'warning' ? { class: 'warning', text: 'CAUTION' } :
        { class: 'danger', text: 'AVOID' };

    categories.push({
      name: 'Inactive ingredients',
      evaluation: excipientBadge,
      expanded: true,
      ingredients: product?.excipients || []
    });

    console.log('Checking if brand has trust_score_details:', !!brand?.trust_score_details);
    if (brand?.trust_score_details) {
      // Manufacturing Standards
      const manufacturingDetail = brand.trust_score_details.find((d: any) => d.category === 'Manufacturing Standards');
      categories.push({
        name: 'Manufacturing Standards',
        evaluation: this.getEvaluationBadge(manufacturingDetail?.rank),
        expanded: false,
        items: this.getManufacturingItems(brand)
      });

      // Testing Benchmarks
      const testingDetail = brand.trust_score_details.find((d: any) => d.category === 'Testing Benchmarks');
      categories.push({
        name: 'Testing Benchmarks',
        evaluation: this.getEvaluationBadge(testingDetail?.rank),
        expanded: false,
        items: this.getTestingItems(brand)
      });

      // Product Quality
      const qualityDetail = product?.trust_score_details?.find((d: any) => d.category === 'Product Quality Indicators') ||
        brand.trust_score_details.find((d: any) => d.category === 'Product Quality Indicators');
      categories.push({
        name: 'Product Quality',
        evaluation: this.getEvaluationBadge(qualityDetail?.rank),
        expanded: false,
        items: this.getProductQualityItems(product, brand)
      });

      // Product Certifications
      const certDetail = product?.trust_score_details?.find((d: any) => d.category === 'Product Level Certifications') ||
        brand.trust_score_details.find((d: any) => d.category === 'Product Level Certifications');
      categories.push({
        name: 'Product Certifications',
        evaluation: this.getEvaluationBadge(certDetail?.rank),
        expanded: false,
        items: this.getProductCertificationItems(product, brand)
      });

      // Technical Innovation
      const techDetail = product?.trust_score_details?.find((d: any) => d.category === 'Technical Innovation') ||
        brand.trust_score_details.find((d: any) => d.category === 'Technical Innovation');
      categories.push({
        name: 'Technical Innovation',
        evaluation: this.getEvaluationBadge(techDetail?.rank),
        expanded: false,
        items: this.getTechnicalInnovationItems(product, brand)
      });

      // Brand Certifications
      const brandCertDetail = brand.trust_score_details.find((d: any) => d.category === 'Brand Level Certifications');
      categories.push({
        name: 'Brand Certifications',
        evaluation: this.getEvaluationBadge(brandCertDetail?.rank),
        expanded: false,
        items: this.getBrandCertificationItems(brand)
      });
    }

    return categories;
  }

  private getManufacturingItems(brand: any): TrustItem[] {
    const items: TrustItem[] = [];
    if (brand?.cgmp === 'Certified') {
      items.push({ name: 'cGMP 3rd-Party Certified', value: true });
    } else if (brand?.cgmp === 'Compliant') {
      items.push({ name: 'cGMP 3rd-Party Certified', value: false });
      items.push({ name: 'cGMP Compliant (Self-Reported)', value: true });
    } else {
      items.push({ name: 'cGMP 3rd-Party Certified', value: false });
      items.push({ name: 'cGMP Compliant (Self-Reported)', value: false });
    }

    if (brand?.made_in_usa === 'Partially') {
      items.push({ name: 'Most Products Made in USA', value: true });
    } else {
      items.push({ name: 'Made in USA', value: brand?.made_in_usa === 'Yes' });
    }

    return items;
  }

  private getTestingItems(_brand: any): TrustItem[] {
    // Return empty array since testing fields aren't available on Brand type
    // In a real implementation, these would come from additional API data
    return [];
  }

  private getProductQualityItems(product: any, _brand: any): TrustItem[] {
    return [
      { name: 'Patented Ingredients', value: product?.patented_or_trademarked_ingredients || false },
      { name: 'Vegan Friendly Ingredients', value: product?.vegan_friendly_ingredients || false },
      { name: 'Organic Ingredients', value: product?.organic_ingredients || false },
      { name: 'Non-GMO Ingredients', value: product?.non_gmo_ingredients || false },
      { name: 'Free from Artificial Flavors, Colors, Fillers', value: product?.free_from_artificial_colors_flavors_fillers || false },
      { name: 'Gluten-Free Ingredients', value: product?.gluten_free_ingredients || false }
    ];
  }

  private getProductCertificationItems(product: any, brand: any): TrustItem[] {
    const items: TrustItem[] = [];
    if (product?.usp_verified || brand?.usp_verified) {
      items.push({ name: 'USP Verified', value: true });
    }
    if (product?.vegan_action_certified || brand?.vegan_action_certified) {
      items.push({ name: 'Vegan Action Certified', value: true });
    }
    if (product?.informed_sport_certified || brand?.informed_sport_certified) {
      items.push({ name: 'Informed Sport Certified', value: true });
    }
    if (product?.usda_organic_certified || brand?.usda_organic_certified) {
      items.push({ name: 'USDA Organic Certified', value: true });
    }
    if (product?.non_gmo_certified || brand?.non_gmo_certified) {
      items.push({ name: 'Non-GMO Certified', value: true });
    }
    if (product?.gluten_free_certified || brand?.gluten_free_certified) {
      items.push({ name: 'Gluten-Free Certified', value: true });
    }
    if (product?.nsf_certified_for_sport || brand?.nsf_certified_for_sport) {
      items.push({ name: 'NSF Certified for Sport', value: true });
    }
    if (product?.informed_choice_certified || brand?.informed_choice_certified) {
      items.push({ name: 'Informed Choice Certified', value: true });
    }
    return items;
  }

  private getTechnicalInnovationItems(product: any, _brand: any): TrustItem[] {
    return [
      { name: 'Founder, CEO, CSO or CMO has a PhD, MD, DO or ND', value: false }, // Not available on Brand type
      { name: 'Clinically Studied Product', value: product?.trust_clinically_studied_product || false },
      { name: 'Practitioner-grade Integration', value: product?.brand_practitioner_grade_integration || false }
    ];
  }

  private getBrandCertificationItems(brand: any): TrustItem[] {
    const items: TrustItem[] = [];
    if (brand?.nsf_certified) items.push({ name: 'NSF Certified', value: true });
    if (brand?.clean_label_project_certified) items.push({ name: 'Clean Label Project Certified', value: true });
    return items;
  }
}

export interface TrustCategory {
  name: string;
  evaluation: {
    class: string;
    text: string;
  };
  expanded: boolean;
  ingredients?: any[];
  items?: TrustItem[];
}

export interface TrustItem {
  name: string;
  value: boolean;
} 